

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { MultiSelectComboBox } from '@/components/multi-select-combobox';
import { useOrganizations } from '@/features/organizations';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { characterCoreSchema } from '@/lib/schemas/character';

export function BasicIdentitySection({ isNameReadonly }: { isNameReadonly?: boolean }) {
  const {
    register,
    watch,
    formState: { errors },
    control,
  } = useFormContext<z.infer<typeof characterCoreSchema>>();
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();
  const { organizations, isLoading: isLoadingOrganizations } = useOrganizations();
  const organizationOptions = organizations.map((organization) => ({
    value: organization.id,
    label: organization.name,
  }));

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          {t('entity_form.characters.labels.main_info_title')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('entity_form.characters.labels.main_info_desc')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <TextField
            {...register('name')}
            label={t('entity_form.characters.labels.name')}
            placeholder={t('entity_form.characters.labels.name_placeholder')}
            fullWidth
            variant="standard"
            error={!!errors.name}
            helperText={errors.name?.message}
            InputLabelProps={{ shrink: true }}
            disabled={isNameReadonly}
            sx={{
              '& .MuiInput-root': {
                height: 40,
                fontSize: '0.9rem',
                '&:before': { borderBottomWidth: 1 },
                '&:after': { borderBottomWidth: 2 }
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
          <Controller
            name="type"
            control={control}
            defaultValue="Supporting"
            render={({ field }) => (
              <FormControl fullWidth variant="standard" error={!!errors.type}>
                <InputLabel shrink>{t('entity_form.characters.labels.type')}</InputLabel>
                <Select
                  {...field}
                  sx={{
                    height: 40,
                    fontSize: '0.9rem',
                    '&:before': { borderBottomWidth: 1 },
                    '&:after': { borderBottomWidth: 2 }
                  }}
                >
                  <MenuItem value="Protagonist">{t('entity_form.characters.roles.protagonist')}</MenuItem>
                  <MenuItem value="Antagonist">{t('entity_form.characters.roles.antagonist')}</MenuItem>
                  <MenuItem value="Supporting">{t('entity_form.characters.roles.supporting')}</MenuItem>
                  <MenuItem value="Minor">{t('entity_form.characters.roles.minor')}</MenuItem>
                </Select>
                {errors.type && <FormHelperText>{errors.type.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 4 })}>
          <TextField
            {...register('fullName')}
            label={t('entity_form.characters.labels.full_name')}
            placeholder={t('entity_form.characters.labels.full_name_placeholder')}
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.fullName}
            helperText={errors.fullName?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': {
                height: 44,
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 4 })}>
          <TextField
            {...register('nickname')}
            label={t('entity_form.characters.labels.nickname')}
            placeholder={t('entity_form.characters.labels.nickname_placeholder')}
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.nickname}
            helperText={errors.nickname?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': {
                height: 44,
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12, md: 4 })}>
          <TextField
            {...register('characterClass')}
            label={t('entity_form.characters.labels.character_class')}
            placeholder={t('entity_form.characters.labels.character_class_placeholder')}
            fullWidth
            variant="filled"
            size="small"
            error={!!errors.characterClass}
            helperText={errors.characterClass?.message}
            InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5, px: 1.5 } }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiFilledInput-root': {
                height: 44,
              }
            }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <Controller
            name="organizationIds"
            control={control}
            render={({ field }) => (
              <Box data-field="organizationIds">
                <MultiSelectComboBox
                  options={organizationOptions}
                  selected={field.value ?? []}
                  onChange={field.onChange}
                  placeholder={t('entity_form.characters.labels.organizations_placeholder')}
                  emptyPlaceholder={t('entity_form.characters.labels.no_organizations_found')}
                  isLoading={isLoadingOrganizations}
                />
              </Box>
            )}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <TextField
            {...register('shortDescription')}
            label={t('entity_form.characters.labels.short_description')}
            placeholder={t('entity_form.characters.labels.short_description_placeholder')}
            fullWidth
            multiline
            minRows={2}
            variant="filled"
            size="small"
            error={!!errors.shortDescription}
            helperText={errors.shortDescription?.message}
            InputProps={{
              disableUnderline: true,
              sx: {
                borderRadius: 1.5,
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid size={resolveResponsive({ xs: 12 })}>
          <TextField
            {...register('visualDescription')}
            label={t('entity_form.characters.labels.visual_description')}
            placeholder={t('entity_form.characters.labels.visual_description_placeholder')}
            fullWidth
            multiline
            minRows={3}
            variant="filled"
            size="small"
            inputProps={{ maxLength: 1500 }}
            error={!!errors.visualDescription}
            helperText={errors.visualDescription?.message || `${(watch('visualDescription') || '').length} / 1500`}
            InputProps={{
              disableUnderline: true,
              sx: {
                borderRadius: 1.5,
                p: 1.5,
                '& textarea': { resize: 'vertical' }
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
